import { useEffect, useState } from "react";
import api, { formatEUR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, Plus, Trash2, Users, UserPlus, Share2, X } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "checking", label: "Compte courant" },
  { value: "savings", label: "Épargne" },
  { value: "cash", label: "Espèces" },
  { value: "credit", label: "Carte de crédit" },
];

function ShareDialog({ account, onChange }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState({ owner: null, members: [] });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/accounts/${account.id}/members`);
    setMembers(data);
  };
  useEffect(() => { if (open) load(); }, [open]);

  const share = async () => {
    if (!email) return;
    setLoading(true);
    try {
      await api.post(`/accounts/${account.id}/share`, { email });
      toast.success(`${email} a maintenant accès`);
      setEmail("");
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (mid) => {
    await api.delete(`/accounts/${account.id}/share/${mid}`);
    toast.success("Membre retiré");
    load();
    onChange?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`account-share-${account.id}`}>
          <Share2 className="w-4 h-4 text-stone-500" />
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Partager · {account.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">Propriétaire</div>
          {members.owner && (
            <div className="flex items-center gap-3 bg-stone-50 rounded-xl p-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-semibold text-sm">
                {members.owner.name?.slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{members.owner.name}</div>
                <div className="text-xs text-stone-500 truncate">{members.owner.email}</div>
              </div>
            </div>
          )}
          <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">Membres</div>
          <div className="space-y-2">
            {members.members.length === 0 && <div className="text-xs text-stone-400 px-2">Aucun membre partagé</div>}
            {members.members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 rounded-xl border border-stone-100 p-3" data-testid={`share-member-${m.id}`}>
                <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center text-stone-700 font-semibold text-sm">
                  {m.name?.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{m.name}</div>
                  <div className="text-xs text-stone-500 truncate">{m.email}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeMember(m.id)} data-testid={`share-remove-${m.id}`}>
                  <X className="w-4 h-4 text-stone-400" />
                </Button>
              </div>
            ))}
          </div>
          <div className="space-y-1.5 pt-2 border-t border-stone-100">
            <Label>Inviter un membre par email</Label>
            <div className="flex gap-2">
              <Input value={email} onChange={(e) => setEmail(e.target.value)}
                     type="email" placeholder="conjoint@example.com"
                     className="rounded-xl h-11" data-testid="share-email-input" />
              <Button onClick={share} disabled={loading || !email}
                      className="rounded-xl bg-stone-900 hover:bg-stone-800 flex-shrink-0" data-testid="share-invite-button">
                <UserPlus className="w-4 h-4 mr-1" /> Inviter
              </Button>
            </div>
            <p className="text-xs text-stone-500 mt-1">L'utilisateur doit déjà avoir un compte Budgeo.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("checking");
  const [balance, setBalance] = useState("");

  const load = async () => {
    const { data } = await api.get("/accounts");
    setAccounts(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!name) return toast.error("Nom requis");
    await api.post("/accounts", {
      name, type, balance: parseFloat(balance) || 0,
      color: "#10B981", icon: "wallet",
    });
    toast.success("Compte créé");
    setOpen(false); setName(""); setBalance("");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce compte ?")) return;
    await api.delete(`/accounts/${id}`);
    load();
  };

  const total = accounts.reduce((s, a) => s + (a.balance || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Comptes</h1>
          <p className="text-stone-500 mt-1 font-mono-num">Solde total : {formatEUR(total)}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="btn-add-account">
              <Plus className="w-4 h-4 mr-1" /> Compte
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl">Nouveau compte</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nom</Label><Input value={name} onChange={(e) => setName(e.target.value)}
                className="rounded-xl h-11 mt-1.5" placeholder="Livret A" data-testid="account-name-input" /></div>
              <div>
                <Label>Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="rounded-xl h-11 mt-1.5" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Solde initial (€)</Label><Input type="number" step="0.01" value={balance}
                onChange={(e) => setBalance(e.target.value)} className="rounded-xl h-11 mt-1.5" data-testid="account-balance-input" /></div>
              <Button onClick={submit} className="w-full h-11 rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="account-submit-button">
                Créer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {accounts.map((a) => (
          <Card key={a.id} className="p-6 rounded-2xl border-stone-200 hover:-translate-y-1 transition-transform" data-testid={`account-${a.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-stone-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-stone-700" />
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {a.name}
                    {a.is_shared && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                        <Users className="w-3 h-3" /> Partagé
                      </span>
                    )}
                    {!a.is_owner && (
                      <span className="text-[10px] uppercase tracking-wider text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full font-medium">
                        Invité
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500">{TYPES.find((t) => t.value === a.type)?.label}</div>
                </div>
              </div>
              <div className="flex items-center">
                {a.is_owner && <ShareDialog account={a} onChange={load} />}
                {a.is_owner && (
                  <Button variant="ghost" size="icon" onClick={() => remove(a.id)} data-testid={`account-delete-${a.id}`}>
                    <Trash2 className="w-4 h-4 text-stone-400" />
                  </Button>
                )}
              </div>
            </div>
            <div className="font-display text-3xl font-bold font-mono-num">{formatEUR(a.balance || 0)}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}
